import { useRef, useState } from "react";
import { AlertTriangle, Building2, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierDispatchCenter,
  useCarrierDispatchCenters,
  useCarrierServiceAreas,
  useCreateCarrierDispatchCenter,
  useDeactivateCarrierDispatchCenter,
  useUpdateCarrierDispatchCenter,
} from "@/api/carrier";
import { ColumnsMenu } from "@/components/data-table";
import { DetailDrawer } from "@/components/detail-drawer";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DispatchCenterFormSheet } from "@/features/dispatch-center-form-sheet";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { hasHttpStatus } from "@/lib/api-error";
import { cn } from "@/lib/utils";

const RESPONSIVE: Record<string, string> = {
  location: "hidden xl:table-cell",
  contact: "hidden xl:table-cell",
  capabilities: "hidden xl:table-cell",
  services: "hidden xl:table-cell",
};

const TOGGLEABLE_COLUMNS = [
  { id: "location", label: "Location" },
  { id: "contact", label: "Contact" },
  { id: "capabilities", label: "Capabilities" },
  { id: "services", label: "Services" },
];

type CenterStatus = "all" | "active" | "inactive";

export default function DispatchCentersScreen() {
  const centers = useCarrierDispatchCenters();
  const serviceAreas = useCarrierServiceAreas({ all: true });
  const create = useCreateCarrierDispatchCenter();
  const update = useUpdateCarrierDispatchCenter();
  const deactivate = useDeactivateCarrierDispatchCenter();
  const [editing, setEditing] = useState<CarrierDispatchCenter | "new" | null>(
    null
  );
  const [viewing, setViewing] = useState<CarrierDispatchCenter | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<CenterStatus>("all");
  const [mutationTarget, setMutationTarget] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const mutationLock = useRef(false);
  const { isVisible, toggle: toggleColumn } = useColumnVisibility(
    "carrier-web:columns:dispatch-centers"
  );
  const colClass = (id: string) =>
    isVisible(id) ? (RESPONSIVE[id] ?? "") : "hidden";
  const rows = centers.data ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const filteredRows = rows.filter((center) => {
    const matchesStatus =
      status === "all" ||
      (status === "active" ? center.isActive : !center.isActive);
    const matchesSearch =
      normalizedSearch.length === 0 ||
      [
        center.name,
        center.code,
        center.serviceArea?.name,
        center.address?.line1,
        center.address?.city,
        center.address?.province,
        center.address?.postalCode,
        center.contactName,
        center.contactEmail,
        center.contactPhone,
        ...center.postalCodes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    return matchesStatus && matchesSearch;
  });
  const activeCount = rows.filter((center) => center.isActive).length;
  const defaultCenter = rows.find((center) => center.isDefault);
  const serviceAreaRows = Array.isArray(serviceAreas.data)
    ? serviceAreas.data
    : (serviceAreas.data?.data ?? []);
  const forbidden = hasHttpStatus(centers.error, 403);
  const blockingError = centers.error && (rows.length === 0 || forbidden);
  const hasFilters = normalizedSearch.length > 0 || status !== "all";

  const handleStatus = async (center: CarrierDispatchCenter) => {
    if (mutationLock.current) return false;
    mutationLock.current = true;
    setMutationTarget(center.id);
    setMutationError(null);
    try {
      if (center.isActive) {
        await deactivate.mutateAsync(center.id);
        toast.success("Dispatch center deactivated.");
      } else {
        await update.mutateAsync({
          dispatchCenterId: center.id,
          body: { isActive: true },
        });
        toast.success("Dispatch center activated.");
      }
      return true;
    } catch (caught) {
      const message = mutationMessage(caught, center.isActive);
      setMutationError(message);
      toast.error(message);
      return false;
    } finally {
      mutationLock.current = false;
      setMutationTarget(null);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
  };

  return (
    <div className="carrier-page">
      <PageHeader
        title="Dispatch centers"
        actions={
          !forbidden ? (
            <Button size="touch" onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" />
              New center
            </Button>
          ) : undefined
        }
      />

      {centers.isLoading && rows.length === 0 ? (
        <DispatchCentersLoading />
      ) : blockingError ? (
        <DispatchCentersError
          forbidden={forbidden}
          onRetry={() => void centers.refetch()}
        />
      ) : (
        <>
          {centers.error && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
            >
              <span>
                Center refresh failed. Loaded dispatch-center data remains
                available.
              </span>
              <Button
                variant="outline"
                size="touch"
                className="bg-background"
                onClick={() => void centers.refetch()}
              >
                Try again
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <SummaryCard label="Centers" value={rows.length} />
            <SummaryCard label="Active" value={activeCount} />
            <SummaryCard
              label="Default"
              value={defaultCenter?.code ?? "—"}
              className="col-span-2 sm:col-span-1"
            />
          </div>

          <Card className="overflow-hidden">
            <CardHeader className="gap-3 space-y-0 border-b p-3 md:px-4 md:py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Building2 className="h-4 w-4" />
                    Center roster
                  </CardTitle>
                  <CardDescription className="mt-1 text-xs">
                    Create, edit, activate, or deactivate owned centers.
                  </CardDescription>
                </div>
                <div className="hidden xl:block">
                  <ColumnsMenu
                    columns={TOGGLEABLE_COLUMNS}
                    isVisible={isVisible}
                    onToggle={toggleColumn}
                  />
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_11rem]">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    aria-label="Search dispatch centers"
                    placeholder="Search center, address, or contact"
                    value={search}
                    className="pl-9 pr-10"
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  {search && (
                    <Button
                      variant="ghost"
                      size="icon-sm-touch"
                      aria-label="Clear search"
                      className="absolute right-0 top-1/2 -translate-y-1/2"
                      onClick={() => setSearch("")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <NativeSelect
                  aria-label="Filter dispatch centers by status"
                  value={status}
                  onChange={(event) =>
                    setStatus(event.target.value as CenterStatus)
                  }
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </NativeSelect>
              </div>
            </CardHeader>

            {mutationError && (
              <p
                role="alert"
                className="border-b bg-destructive/5 p-3 text-sm text-destructive"
              >
                {mutationError}
              </p>
            )}

            {rows.length === 0 ? (
              <EmptyCenters onCreate={() => setEditing("new")} />
            ) : filteredRows.length === 0 ? (
              <NoCenterResults hasFilters={hasFilters} onClear={resetFilters} />
            ) : (
              <>
                <div className="space-y-2 p-3 md:hidden">
                  {filteredRows.map((center) => (
                    <DispatchCenterCard
                      key={center.id}
                      center={center}
                      mutationTarget={mutationTarget}
                      onView={() => setViewing(center)}
                      onEdit={() => setEditing(center)}
                      onStatus={() => void handleStatus(center)}
                    />
                  ))}
                </div>
                <CardContent className="hidden p-0 md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pl-4">Center</TableHead>
                        <TableHead className={colClass("location")}>
                          Location
                        </TableHead>
                        <TableHead className={colClass("contact")}>
                          Contact
                        </TableHead>
                        <TableHead className={colClass("capabilities")}>
                          Capabilities
                        </TableHead>
                        <TableHead className={colClass("services")}>
                          Services
                        </TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-12 pr-4" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRows.map((center) => {
                        const pending = mutationTarget === center.id;
                        return (
                          <TableRow
                            key={center.id}
                            aria-busy={pending || undefined}
                          >
                            <TableCell className="pl-4">
                              <div className="font-medium">{center.name}</div>
                              <div className="font-mono text-xs text-muted-foreground">
                                {center.code}
                                {center.isDefault ? " · Default" : ""}
                              </div>
                              <dl className="mt-2 grid max-w-xl grid-cols-2 gap-x-3 gap-y-1 text-xs xl:hidden">
                                <CenterDetail
                                  label="Location"
                                  value={formatAddress(center)}
                                />
                                <CenterDetail
                                  label="Service area"
                                  value={
                                    center.serviceArea?.name ?? "Not assigned"
                                  }
                                />
                                <CenterDetail
                                  label="Contact"
                                  value={formatContact(center)}
                                />
                                <CenterDetail
                                  label="Capabilities"
                                  value={formatCapabilities(center)}
                                />
                              </dl>
                            </TableCell>
                            <TableCell
                              className={cn("text-sm", colClass("location"))}
                            >
                              <div>{formatAddress(center)}</div>
                              <div className="text-xs text-muted-foreground">
                                {center.serviceArea?.name ?? "No service area"}
                              </div>
                            </TableCell>
                            <TableCell
                              className={cn("text-sm", colClass("contact"))}
                            >
                              {formatContact(center)}
                            </TableCell>
                            <TableCell
                              className={cn(
                                "text-sm",
                                colClass("capabilities")
                              )}
                            >
                              {formatCapabilities(center)}
                            </TableCell>
                            <TableCell
                              className={cn("text-sm", colClass("services"))}
                            >
                              {formatServiceCodes(center)}
                            </TableCell>
                            <TableCell>
                              <StatusBadge
                                status={center.isActive ? "ACTIVE" : "INACTIVE"}
                              />
                            </TableCell>
                            <TableCell className="pr-4">
                              {pending && (
                                <span role="status" className="sr-only">
                                  Saving changes for {center.name}…
                                </span>
                              )}
                              <RowMenu
                                label={`Actions for ${center.name}`}
                                disabled={mutationTarget !== null}
                              >
                                <RowMenuItem
                                  onSelect={() => setEditing(center)}
                                >
                                  Edit center
                                </RowMenuItem>
                                <RowMenuItem
                                  onSelect={() => void handleStatus(center)}
                                >
                                  {center.isActive ? "Deactivate" : "Activate"}
                                </RowMenuItem>
                              </RowMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </>
            )}
          </Card>
        </>
      )}

      {editing && (
        <DispatchCenterFormSheet
          center={editing === "new" ? null : editing}
          serviceAreas={serviceAreaRows}
          serviceAreasLoading={serviceAreas.isLoading}
          serviceAreasError={serviceAreas.error}
          onClose={() => setEditing(null)}
          onSubmit={async (center, body) => {
            if (center) {
              await update.mutateAsync({
                dispatchCenterId: center.id,
                body,
              });
              toast.success("Dispatch center updated.");
            } else {
              await create.mutateAsync(body);
              toast.success("Dispatch center created.");
            }
            setEditing(null);
          }}
        />
      )}
      {viewing && (
        <DispatchCenterDetailSheet
          center={viewing}
          pending={mutationTarget === viewing.id}
          mutationsBlocked={mutationTarget !== null}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setViewing(null);
            setEditing(viewing);
          }}
          onStatus={() => {
            void handleStatus(viewing).then((succeeded) => {
              if (succeeded) setViewing(null);
            });
          }}
        />
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  className,
}: {
  label: string;
  value: number | string;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex-row items-center justify-between space-y-0 p-3">
        <CardDescription className="text-xs">{label}</CardDescription>
        <CardTitle className="text-xl tabular-nums">{value}</CardTitle>
      </CardHeader>
    </Card>
  );
}

function DispatchCenterCard({
  center,
  mutationTarget,
  onView,
  onEdit,
  onStatus,
}: {
  center: CarrierDispatchCenter;
  mutationTarget: string | null;
  onView: () => void;
  onEdit: () => void;
  onStatus: () => void;
}) {
  const pending = mutationTarget === center.id;
  const mutationsBlocked = mutationTarget !== null;
  return (
    <article
      className="rounded-lg border bg-background p-3"
      aria-busy={pending || undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{center.name}</h3>
          <p className="font-mono text-xs text-muted-foreground">
            {center.code}
            {center.isDefault ? " · Default" : ""}
          </p>
        </div>
        <StatusBadge status={center.isActive ? "ACTIVE" : "INACTIVE"} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <CenterDetail label="Address" value={formatAddress(center)} />
        <CenterDetail
          label="Service area"
          value={center.serviceArea?.name ?? "Not assigned"}
        />
      </dl>
      {pending && (
        <p role="status" className="mt-2 text-xs text-muted-foreground">
          Saving changes…
        </p>
      )}
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Button
          variant="outline"
          size="sm-touch"
          className="px-2"
          onClick={onView}
        >
          Details
        </Button>
        <Button
          variant="outline"
          size="sm-touch"
          className="px-2"
          disabled={mutationsBlocked}
          onClick={onEdit}
        >
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm-touch"
          className="px-2"
          disabled={mutationsBlocked}
          onClick={onStatus}
        >
          {pending ? "Saving…" : center.isActive ? "Deactivate" : "Activate"}
        </Button>
      </div>
    </article>
  );
}

function DispatchCenterDetailSheet({
  center,
  pending,
  mutationsBlocked,
  onClose,
  onEdit,
  onStatus,
}: {
  center: CarrierDispatchCenter;
  pending: boolean;
  mutationsBlocked: boolean;
  onClose: () => void;
  onEdit: () => void;
  onStatus: () => void;
}) {
  return (
    <DetailDrawer
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
      title={center.name}
      description={`${center.code}${center.isDefault ? " · Default center" : ""}`}
    >
      <dl className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
        <CenterDetail label="Address" value={formatAddress(center)} />
        <CenterDetail
          label="Service area"
          value={center.serviceArea?.name ?? "Not assigned"}
        />
        <CenterDetail label="Contact" value={formatContact(center)} />
        <CenterDetail label="Capabilities" value={formatCapabilities(center)} />
        <CenterDetail label="Time zone" value={center.timeZone} />
        <CenterDetail
          label="Processing"
          value={`${center.processingMinutes} minutes`}
        />
        <CenterDetail
          label="Postal prefixes"
          value={center.postalCodes.join(", ") || "None listed"}
        />
        <CenterDetail
          label="Status"
          value={center.isActive ? "Active" : "Inactive"}
        />
      </dl>
      {pending && (
        <p role="status" className="mt-4 text-sm text-muted-foreground">
          Saving changes…
        </p>
      )}
      <div className="mt-5 grid grid-cols-2 gap-2 border-t pt-4">
        <Button
          variant="outline"
          size="touch"
          disabled={mutationsBlocked}
          onClick={onEdit}
        >
          Edit center
        </Button>
        <Button
          variant="outline"
          size="touch"
          disabled={mutationsBlocked}
          onClick={onStatus}
        >
          {pending ? "Saving…" : center.isActive ? "Deactivate" : "Activate"}
        </Button>
      </div>
    </DetailDrawer>
  );
}

function CenterDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words font-medium">{value}</dd>
    </div>
  );
}

function DispatchCentersLoading() {
  return (
    <div
      role="status"
      aria-label="Loading dispatch centers"
      className="space-y-3"
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {["centers", "active", "default"].map((summary) => (
          <Skeleton key={summary} className="h-16" />
        ))}
      </div>
      <Skeleton className="h-72" />
    </div>
  );
}

function DispatchCentersError({
  forbidden,
  onRetry,
}: {
  forbidden: boolean;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardContent
        role="alert"
        className="flex flex-col items-center gap-3 p-8 text-center"
      >
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <div>
          <h2 className="font-semibold">
            {forbidden
              ? "Dispatch centers unavailable"
              : "Couldn’t load dispatch centers"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {forbidden
              ? "You do not have permission to view carrier dispatch centers."
              : "Check your connection and try again."}
          </p>
        </div>
        {!forbidden && (
          <Button variant="outline" size="touch" onClick={onRetry}>
            Try again
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyCenters({ onCreate }: { onCreate: () => void }) {
  return (
    <CardContent role="status" className="p-8 text-center">
      <Building2 className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
      <h2 className="font-semibold">No dispatch centers yet</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Create an owned operating location to configure coverage and
        capabilities.
      </p>
      <Button size="touch" className="mt-4" onClick={onCreate}>
        Create your first center
      </Button>
    </CardContent>
  );
}

function NoCenterResults({
  hasFilters,
  onClear,
}: {
  hasFilters: boolean;
  onClear: () => void;
}) {
  return (
    <CardContent role="status" className="p-8 text-center">
      <Search className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
      <h2 className="font-semibold">No matching dispatch centers</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Try another center, address, contact, or status.
      </p>
      {hasFilters && (
        <Button
          variant="outline"
          size="touch"
          className="mt-4"
          onClick={onClear}
        >
          Clear filters
        </Button>
      )}
    </CardContent>
  );
}

function formatAddress(center: CarrierDispatchCenter): string {
  if (!center.address) return "No address";
  return [
    center.address.line1,
    center.address.line2,
    center.address.city,
    center.address.province,
    center.address.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
}

function formatContact(center: CarrierDispatchCenter): string {
  const contact = [center.contactName, center.contactEmail, center.contactPhone]
    .filter(Boolean)
    .join(" · ");
  return contact || "No contact";
}

function formatCapabilities(center: CarrierDispatchCenter): string {
  const labels = [
    center.canHandlePickup ? "Pickup" : null,
    center.canHandleDelivery ? "Delivery" : null,
    center.canHandleReturn ? "Returns" : null,
    center.canHandleTransfer ? "Transfers" : null,
  ].filter(Boolean);
  return labels.length > 0 ? labels.join(", ") : "None enabled";
}

/**
 * Which services actually route through this centre.
 *
 * Distinct from its capabilities: a centre can be delivery-capable and still
 * have no service routed through it, so "None configured" is a real state
 * rather than missing data.
 */
function formatServiceCodes(center: CarrierDispatchCenter): string {
  const codes = center.serviceCodes ?? [];
  return codes.length > 0 ? codes.join(", ") : "None configured";
}

function mutationMessage(error: unknown, wasActive: boolean): string {
  if (hasHttpStatus(error, 403)) {
    return "You do not have permission to manage dispatch centers.";
  }
  const response = error as { response?: { data?: { error?: string } } };
  return (
    response.response?.data?.error ??
    `Dispatch center could not be ${wasActive ? "deactivated" : "activated"}.`
  );
}
